import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosRequestHeaders,
  type InternalAxiosRequestConfig,
} from 'axios';

import { authEvents } from '../lib/authEvents';
import { SESSION_TOKEN } from '../lib/sessionToken';
import { AuthService, serviceOptions } from './service';

import { tokenStorage } from '@/entities/auth/model/tokenStorage';

const API_URL =
  import.meta.env.VITE_APP_ENV !== 'prod'
    ? import.meta.env.VITE_API_URL
    : `${window.location.protocol}//api.${window.location.hostname.replace('www.', '')}/`;

const instance = axios.create({
  baseURL: API_URL,
  withCredentials: false,
});

instance.interceptors.request.use((config) => {
  const sessionToken = window.localStorage.getItem(SESSION_TOKEN);
  if (sessionToken) {
    // eslint-disable-next-line no-param-reassign
    config.headers['X-SESSION-ID'] = sessionToken;
  }
  return config;
});

function applyAuthHeader(
  headers: AxiosRequestHeaders | undefined,
  token: string
): AxiosRequestHeaders {
  const h = AxiosHeaders.from(headers);
  h.set('Authorization', `Bearer ${token}`);
  return h;
}

instance.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (!token) return config;

  const headers = applyAuthHeader(config.headers, token);

  return { ...config, headers } as typeof config;
});

type RequestConfigWithRetry = InternalAxiosRequestConfig & { retry?: boolean };

instance.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalRequest = (error.config || {}) as RequestConfigWithRetry;
    const status = error.response?.status;

    if ((status === 401 || status === 403) && !originalRequest.retry) {
      originalRequest.retry = true;

      try {
        const refresh = localStorage.getItem('refreshToken');
        const phone = localStorage.getItem('phone');
        if (!refresh || !phone)
          throw new Error('Нет refresh-токена или телефона');

        const pair = await AuthService.v1AuthRefreshCreate({
          body: { phone, refresh_token: refresh },
        });

        const newAccess = pair.access_token;
        const newRefresh = pair.refresh_token;
        if (!newAccess || !newRefresh)
          throw new Error('Не удалось обновить токены');

        tokenStorage.save({ access: newAccess, refresh: newRefresh, phone });

        originalRequest.headers = applyAuthHeader(
          originalRequest.headers,
          newAccess
        );

        return await instance(originalRequest);
      } catch {
        tokenStorage.clear();
        authEvents.emit('SESSION_EXPIRED');
      }
    }

    return Promise.reject(error);
  }
);

serviceOptions.axios = instance;
export { instance };
